/* eslint-disable */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { peekNextNumber, advanceNumber } from '../utils/docNumbering';
import useFocusRecord from '../utils/useFocusRecord';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import { useSortableData } from '../hooks/useSortableData';
import { hasPermission, applyViewScope } from '../utils/permissions';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import RequestCancellationButton from '../components/RequestCancellationButton';
import FindDuplicatesButton from '../components/FindDuplicatesButton';
import PaymentReceiptPrint from '../components/payments/PaymentReceiptPrint';

const blankForm = () => ({
  // Type toggle
  payment_type: 'invoice_payment', // 'invoice_payment' | 'advance'
  // Invoice-payment fields
  invoice_number: '',
  customer_name: '',
  invoice_amount: 0,
  // Advance fields
  customer_id: '',
  po_number: '',
  notes: '',
  // Common
  received_amount: 0,
  received_date: new Date().toISOString().split('T')[0],
  payment_mode: 'Cash',
  payment_status: 'Full',
  balance_amount: 0,
  record_no: '',
  receipt_no: '',
  cheque_no: '',
  cheque_date: '',
  cheque_deposit_date: '',
  transfer_date: '',
  bank_name: '',
});

export default function Payments() {
  const { user } = useAuth();
  const { formatAmount, companySettings } = useCurrency();
  const currencyCode = companySettings?.currency || 'USD';
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [showInvoiceDropdown, setShowInvoiceDropdown] = useState(false);
  // Payment currently being viewed in the read-only details modal.
  const [viewingPayment, setViewingPayment] = useState(null);
  // Payment currently in the print-receipt iframe modal.
  const [printingPayment, setPrintingPayment] = useState(null);
  // 'all' | 'invoice_payment' | 'advance'
  const [typeFilter, setTypeFilter] = useState('all');
  // Additional filters
  const [modeFilter, setModeFilter] = useState('all'); // Cash | Cheque | Bank Transfer
  const [customerFilter, setCustomerFilter] = useState('all'); // customer_name
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const { selectedFy } = useFiscalYear();

  // Sync global Fiscal Year selection → list date filter.
  useEffect(() => {
    if (!selectedFy) return;
    setFromDate(selectedFy.start_date);
    setToDate(selectedFy.end_date);
  }, [selectedFy?.id]);

  const [formData, setFormData] = useState(blankForm());

  const fetchPayments = useCallback(async () => {
    try {
      const response = await api.get('/payments');
      setPayments(response.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await api.get('/sales/invoices');
      setInvoices(response.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchInvoices();
    fetchCustomers();
  }, [fetchPayments, fetchInvoices, fetchCustomers]);

  const handleInvoiceSelect = (invoice) => {
    const invoiceAmount = invoice.total_amount || 0;
    // Sum what has already been received on this invoice — excludes the row
    // currently being edited (so editing a partial doesn't double-count).
    const alreadyPaid = payments.reduce((sum, p) => {
      if ((p.payment_type || 'invoice_payment') === 'advance') return sum;
      if (p.invoice_number !== invoice.invoice_number) return sum;
      if (editingPaymentId && p.id === editingPaymentId) return sum;
      if ((p.cancellation_status || '') === 'cancelled') return sum;
      return sum + (p.received_amount || 0);
    }, 0);
    const advanceApplied = invoice.advance_payment || 0;
    const remaining = Math.max(invoiceAmount - alreadyPaid - advanceApplied, 0);
    const receivedAmount = formData.received_amount || 0;
    const balance = remaining - receivedAmount;
    
    setFormData({
      ...formData,
      invoice_number: invoice.invoice_number,
      customer_name: invoice.customer_name,
      invoice_amount: invoiceAmount,
      already_paid: alreadyPaid + advanceApplied,
      remaining_amount: remaining,
      balance_amount: balance,
      payment_status: balance <= 0 ? 'Full' : 'Partial'
    });
    setInvoiceSearch(invoice.invoice_number);
    setShowInvoiceDropdown(false);
  };

  const handleReceivedAmountChange = (value) => {
    const received = parseFloat(value) || 0;
    // Balance reflects what's still outstanding after this receipt against
    // the REMAINING (not the original gross), so multi-part payments
    // correctly close to zero on the final receipt.
    const base = formData.remaining_amount ?? formData.invoice_amount;
    const balance = base - received;
    
    setFormData({
      ...formData,
      received_amount: received,
      balance_amount: balance,
      payment_status: balance <= 0 ? 'Full' : 'Partial'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build payload based on the form type
      const payload = (formData.payment_type === 'advance')
        ? {
            payment_type: 'advance',
            customer_id: formData.customer_id,
            customer_name: formData.customer_name,
            po_number: (formData.po_number || '').trim(),
            received_amount: parseFloat(formData.received_amount) || 0,
            received_date: formData.received_date,
            payment_mode: formData.payment_mode,
            record_no: formData.record_no || null,
            receipt_no: formData.receipt_no || null,
            cheque_no: formData.cheque_no || null,
            cheque_date: formData.cheque_date || null,
            cheque_deposit_date: formData.cheque_deposit_date || null,
            transfer_date: formData.transfer_date || null,
            bank_name: formData.bank_name || null,
            notes: formData.notes || null,
          }
        : {
            payment_type: 'invoice_payment',
            invoice_number: formData.invoice_number,
            customer_name: formData.customer_name,
            invoice_amount: parseFloat(formData.invoice_amount) || 0,
            received_amount: parseFloat(formData.received_amount) || 0,
            received_date: formData.received_date,
            payment_mode: formData.payment_mode,
            payment_status: formData.payment_status,
            balance_amount: parseFloat(formData.balance_amount) || 0,
            record_no: formData.record_no || null,
            receipt_no: formData.receipt_no || null,
            cheque_no: formData.cheque_no || null,
            cheque_date: formData.cheque_date || null,
            cheque_deposit_date: formData.cheque_deposit_date || null,
            transfer_date: formData.transfer_date || null,
            bank_name: formData.bank_name || null,
          };

      if (editingPaymentId) {
        await api.put(`/payments/${editingPaymentId}`, payload);
        alert('Payment updated successfully!');
      } else {
        await api.post('/payments', payload);
        // Bump the customer-payment series counter (matches the Record No.,
        // not the Receipt No which is now a separate manual-entry field).
        advanceNumber('customer_payment', payload.record_no);
        alert(payload.payment_type === 'advance' ? 'Advance recorded successfully!' : 'Payment recorded successfully!');
      }
      await fetchPayments();
      setShowForm(false);
      setEditingPaymentId(null);
      setFormData(blankForm());
      setInvoiceSearch('');
    } catch (error) {
      alert('Error saving payment: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Admin-only: open the existing form prefilled with the payment's data
  const handleEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setFormData({
      payment_type: payment.payment_type || 'invoice_payment',
      invoice_number: payment.invoice_number || '',
      customer_name: payment.customer_name || '',
      customer_id: payment.customer_id || '',
      po_number: payment.po_number || '',
      notes: payment.notes || '',
      invoice_amount: payment.invoice_amount || 0,
      received_amount: payment.received_amount || 0,
      received_date: payment.received_date
        ? new Date(payment.received_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      payment_mode: payment.payment_mode || 'Cash',
      payment_status: payment.payment_status || 'Full',
      balance_amount: payment.balance_amount || 0,
      record_no: payment.record_no || '',
      receipt_no: payment.receipt_no || '',
      cheque_no: payment.cheque_no || '',
      cheque_date: payment.cheque_date
        ? new Date(payment.cheque_date).toISOString().split('T')[0]
        : '',
      cheque_deposit_date: payment.cheque_deposit_date
        ? new Date(payment.cheque_deposit_date).toISOString().split('T')[0]
        : '',
      transfer_date: payment.transfer_date
        ? new Date(payment.transfer_date).toISOString().split('T')[0]
        : '',
      bank_name: payment.bank_name || '',
    });
    setInvoiceSearch(payment.invoice_number || '');
    setShowForm(true);
  };

  // Diagnostics "Open ↗" deep-link: auto-open this payment's edit modal
  // when ?focus=<id-or-receipt_no> is present.
  useFocusRecord(payments, handleEditPayment);

  // Admin-only: hard delete a payment
  const handleDeletePayment = async (payment) => {
    const label = payment.payment_type === 'advance'
      ? `advance against PO ${payment.po_number || '—'}`
      : `payment for invoice #${payment.invoice_number}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/payments/${payment.id}`);
      alert('Payment deleted successfully!');
      await fetchPayments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting payment');
    }
  };

  const handleRefundAdvance = async (payment) => {
    const refundRef = window.prompt('Refund reference (optional):', payment.receipt_no || '');
    if (refundRef === null) return; // user cancelled
    try {
      await api.post(`/payments/${payment.id}/refund`, { refund_ref: refundRef || null });
      alert('Advance marked as refunded');
      await fetchPayments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error refunding advance');
    }
  };

  const filteredInvoices = invoices.filter(inv =>
    inv.invoice_number.toString().toLowerCase().includes(invoiceSearch.toLowerCase()) ||
    inv.customer_name.toLowerCase().includes(invoiceSearch.toLowerCase())
  ).filter(inv => inv.payment_status !== 'Paid');  // Only show unpaid/partially paid invoices

  const filteredPayments = applyViewScope(payments, user).filter(payment => {
    // Type filter chip
    const pType = payment.payment_type || 'invoice_payment';
    if (typeFilter !== 'all' && pType !== typeFilter) return false;
    // Payment mode
    if (modeFilter !== 'all' && (payment.payment_mode || '') !== modeFilter) return false;
    // Customer
    if (customerFilter !== 'all' && (payment.customer_name || '') !== customerFilter) return false;
    // Date range (on received_date)
    if (fromDate || toDate) {
      const recv = payment.received_date ? new Date(payment.received_date) : null;
      if (!recv) return false;
      if (fromDate && recv < new Date(fromDate + 'T00:00:00')) return false;
      if (toDate && recv > new Date(toDate + 'T23:59:59')) return false;
    }
    // Search filter — match invoice_number, po_number, customer_name
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      (payment.invoice_number || '').toLowerCase().includes(q) ||
      (payment.customer_name || '').toLowerCase().includes(q) ||
      (payment.po_number || '').toLowerCase().includes(q)
    );
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Auto-curated dropdown options
  const customerNameOptions = Array.from(new Set(payments.map(p => p.customer_name).filter(Boolean))).sort();

  const {
    sortedItems: sortedPayments,
    requestSort: requestPaySort,
    getSortIndicator: payInd,
  } = useSortableData(filteredPayments);

  /**
   * Running-balance map: for each `invoice_payment` row, compute the balance
   * remaining on the parent invoice **after this payment was applied**, in
   * chronological order. This is what users actually want to see in the list
   * — not the stored `balance_amount` (which is computed at entry-time against
   * the gross invoice and can be wrong when multiple payments exist).
   *
   * Keyed by payment id → { displayBalance, displayStatus, runningReceived }.
   * Cancelled payment rows are skipped from the accumulator so a cancelled
   * receipt doesn't reduce the balance.
   */
  const runningByPaymentId = useMemo(() => {
    // Snapshot of all non-advance, non-cancelled payments sorted by received_date asc, then created_at asc.
    //
    // Note: undeposited cheques DO contribute to the running balance here —
    // the row math represents the eventual balance once every receipt
    // has cleared. The `⏰ Cheque pending deposit` badge separately tells
    // the user the cheque hasn't actually credited the ledger yet. The
    // backend ledger / invoice payment_status apply their own
    // funds-in-transit gating (see `_is_payment_active`).
    const invPayments = (payments || [])
      .filter((p) => (p.payment_type || 'invoice_payment') !== 'advance')
      .filter((p) => (p.cancellation_status || '') !== 'cancelled')
      .slice()
      .sort((a, b) => {
        const da = a.received_date || '';
        const db = b.received_date || '';
        if (da !== db) return da < db ? -1 : 1;
        const ca = a.created_at || '';
        const cb = b.created_at || '';
        return ca < cb ? -1 : ca > cb ? 1 : 0;
      });
    const accReceivedByInv = {};
    const out = {};
    for (const p of invPayments) {
      const inv = p.invoice_number;
      const isPendingCheque = p.payment_mode === 'Cheque' && !p.cheque_deposit_date;
      const prev = accReceivedByInv[inv] || 0;
      const after = prev + (p.received_amount || 0);
      accReceivedByInv[inv] = after;
      const gross = p.invoice_amount || 0;
      const bal = Math.max(gross - after, 0);
      out[p.id] = {
        displayBalance: bal,
        runningReceived: after,
        displayStatus: bal <= 0.0001 ? 'Full' : 'Partial',
        isPendingCheque,
      };
    }
    return out;
  }, [payments]);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">💳 {(user?.role === 'admin' || user?.role === 'super_admin' || user?.view_scope === 'all') ? 'Payments' : 'My Invoice Payments'}</h1>
        <div className="flex gap-2 items-center">
          <FindDuplicatesButton
            module="payments"
            labelFields={['customer_name', 'amount', 'payment_date', 'invoice_number', 'po_number', 'payment_type']}
            onAfterDelete={fetchPayments}
          />
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <button
              onClick={async () => {
                if (!window.confirm('Recompute payment status / received / balance for EVERY existing invoice using the new multi-payment + cheque-deposit rules?\n\nSafe to run multiple times — it only updates invoices that actually have stale values.')) return;
                try {
                  const res = await api.post('/payments/admin/recompute-invoice-statuses');
                  const { invoices_scanned, invoices_changed, sample_changes } = res.data || {};
                  let msg = `✅ Done.\n\nScanned: ${invoices_scanned}\nChanged: ${invoices_changed}`;
                  if (sample_changes?.length) {
                    msg += '\n\nSample diffs:\n' + sample_changes.slice(0, 10).map((c) => {
                      const b = c.before || {}; const a = c.after || {};
                      return `${c.invoice_number}: ${b.payment_status || '—'} → ${a.payment_status || '—'} (bal ${b.balance_amount ?? '—'} → ${a.balance_amount ?? '—'})`;
                    }).join('\n');
                  }
                  alert(msg);
                  await fetchPayments();
                  await fetchInvoices();
                } catch (e) {
                  alert('Failed: ' + (e?.response?.data?.detail || e.message));
                }
              }}
              className="px-3 py-2 rounded border border-violet-600 text-violet-700 hover:bg-violet-50 text-sm"
              title="Walk every invoice and re-stamp payment_status / total_received / balance_amount using the new multi-payment + cheque-deposit rules."
              data-testid="payments-recompute-statuses-btn"
            >
              🔄 Recompute Invoice Statuses
            </button>
          )}
          {hasPermission(user, 'payments', 'create') && (
            <button onClick={() => {
              setEditingPaymentId(null);
              setShowForm(true);
              // Auto-fill the Record No. from the customer-payment series.
              // The Receipt No. field stays empty for manual entry as before.
              peekNextNumber('customer_payment').then((n) => {
                if (n) setFormData((prev) => prev.record_no ? prev : { ...prev, record_no: n });
              });
            }} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
              + Record Payment
            </button>
          )}
        </div>
      </div>

      {/* Payment Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingPaymentId ? '✏️ Edit Payment (Admin)'
                : (formData.payment_type === 'advance' ? '💰 Record Advance (against Customer PO)' : 'Record Payment')}
            </h2>

            {/* Type toggle — only when creating a fresh payment */}
            {!editingPaymentId && (
              <div className="flex gap-2 mb-4" data-testid="payment-type-toggle">
                <button
                  type="button"
                  onClick={() => setFormData({ ...blankForm(), payment_type: 'invoice_payment' })}
                  className={`px-3 py-2 rounded text-sm font-medium border ${formData.payment_type === 'invoice_payment' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid="payment-type-invoice"
                >
                  📄 Payment against Invoice
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...blankForm(), payment_type: 'advance' })}
                  className={`px-3 py-2 rounded text-sm font-medium border ${formData.payment_type === 'advance' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid="payment-type-advance"
                >
                  💰 Advance (against PO)
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                {formData.payment_type === 'invoice_payment' ? (
                  <>
                    {/* Invoice Number - Searchable Dropdown */}
                    <div className="col-span-2 relative">
                      <label className="block text-sm font-medium mb-1">Invoice Number *</label>
                      <input
                        type="text"
                        required
                        placeholder="Search invoice number..."
                        value={invoiceSearch}
                        onChange={(e) => {
                          setInvoiceSearch(e.target.value);
                          setShowInvoiceDropdown(true);
                        }}
                        onFocus={() => setShowInvoiceDropdown(true)}
                        className="w-full border rounded px-3 py-2"
                        data-testid="payment-invoice-search"
                      />
                      {showInvoiceDropdown && filteredInvoices.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
                          {filteredInvoices.map(inv => (
                            <div
                              key={inv.invoice_number}
                              onClick={() => handleInvoiceSelect(inv)}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b"
                            >
                              <div className="font-medium">#{inv.invoice_number}</div>
                              <div className="text-sm text-gray-600">
                                {inv.customer_name} • {formatAmount(inv.total_amount)} • {inv.payment_status}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Customer Name - Auto-filled */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Customer Name</label>
                      <input
                        type="text"
                        value={formData.customer_name}
                        readOnly
                        className="w-full border rounded px-3 py-2 bg-gray-100"
                      />
                    </div>

                    {/* Invoice Amount - Auto-filled */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Invoice Amount ({currencyCode})</label>
                      <input
                        type="number"
                        value={formData.invoice_amount}
                        readOnly
                        className="w-full border rounded px-3 py-2 bg-gray-100"
                      />
                      {formData.invoice_number && (formData.already_paid || 0) > 0 && (
                        <div className="mt-1 text-xs text-amber-700" data-testid="invoice-already-paid-hint">
                          Already received: <b>{formatAmount(formData.already_paid || 0)}</b> &nbsp;•&nbsp;
                          Remaining: <b className="text-rose-700">{formatAmount(formData.remaining_amount || 0)}</b>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Advance: customer dropdown + PO number + notes */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Customer *</label>
                      <select
                        required
                        value={formData.customer_id}
                        onChange={(e) => {
                          const c = customers.find((x) => x.id === e.target.value);
                          setFormData({ ...formData, customer_id: e.target.value, customer_name: c?.name || '' });
                        }}
                        className="w-full border rounded px-3 py-2"
                        data-testid="advance-customer-select"
                      >
                        <option value="">Select customer…</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Customer's PO Number *</label>
                      <input
                        type="text"
                        required
                        value={formData.po_number}
                        onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        placeholder="e.g. PO-CUST-554"
                        data-testid="advance-po-number"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Notes</label>
                      <input
                        type="text"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        placeholder="e.g. 25% deposit before PO confirmation"
                        data-testid="advance-notes"
                      />
                    </div>
                  </>
                )}

                {/* Received Amount */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {formData.payment_type === 'advance' ? `Advance Amount (${currencyCode}) *` : `Received Amount (${currencyCode}) *`}
                  </label>
                  <input
                    type="number"
                    required
                    step="0.001"
                    value={formData.received_amount}
                    onChange={(e) => handleReceivedAmountChange(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    data-testid="payment-received-amount"
                  />
                </div>

                {/* Received Date */}
                <div>
                  <label className="block text-sm font-medium mb-1">Received Date *</label>
                  <DateInputDMY required
 value={formData.received_date}
 onChange={(e) => setFormData({...formData, received_date: e.target.value})}
 className="w-full border rounded px-3 py-2"
 />
                </div>

                {/* Payment Mode */}
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Mode *</label>
                  <select
                    value={formData.payment_mode}
                    onChange={(e) => setFormData({...formData, payment_mode: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                  </select>
                </div>

                {/* Record No — system-generated by the customer-payment numbering rule */}
                <div>
                  <label className="block text-sm font-medium mb-1">Record No.</label>
                  <input
                    type="text"
                    value={formData.record_no}
                    onChange={(e) => setFormData({ ...formData, record_no: e.target.value })}
                    className="w-full border rounded px-3 py-2 font-mono"
                    placeholder="auto-generated (e.g. RV/ZT/00001/26)"
                    data-testid="payment-record-no"
                  />
                </div>

                {/* Receipt No — applies to all payment modes */}
                <div>
                  <label className="block text-sm font-medium mb-1">Receipt No</label>
                  <input
                    type="text"
                    value={formData.receipt_no}
                    onChange={(e) => setFormData({ ...formData, receipt_no: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g. RCT-001"
                    data-testid="payment-receipt-no"
                  />
                </div>

                {/* Cheque-only fields — only relevant when payment_mode === 'Cheque' */}
                {formData.payment_mode === 'Cheque' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Cheque No</label>
                      <input
                        type="text"
                        value={formData.cheque_no}
                        onChange={(e) => setFormData({ ...formData, cheque_no: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        placeholder="e.g. 100345"
                        data-testid="payment-cheque-no"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Cheque Date</label>
                      <input
                        type="date"
                        value={formData.cheque_date}
                        onChange={(e) => setFormData({ ...formData, cheque_date: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        data-testid="payment-cheque-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Deposit Date</label>
                      <input
                        type="date"
                        value={formData.cheque_deposit_date}
                        onChange={(e) => setFormData({ ...formData, cheque_deposit_date: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        data-testid="payment-cheque-deposit-date"
                      />
                    </div>
                  </>
                )}

                {/* Bank-Transfer only fields */}
                {formData.payment_mode === 'Bank Transfer' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Transfer Date</label>
                      <input
                        type="date"
                        value={formData.transfer_date}
                        onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        data-testid="payment-transfer-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        placeholder="e.g. NBB / HSBC / BBK"
                        data-testid="payment-bank-name"
                      />
                    </div>
                  </>
                )}

                {/* Payment Status & Balance — invoice payments only */}
                {formData.payment_type === 'invoice_payment' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Payment Status</label>
                      <input
                        type="text"
                        value={formData.payment_status}
                        readOnly
                        className="w-full border rounded px-3 py-2 bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Balance Amount ({currencyCode})</label>
                      <input
                        type="number"
                        value={(formData.balance_amount || 0).toFixed(3)}
                        readOnly
                        className="w-full border rounded px-3 py-2 bg-gray-100 font-semibold"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-2 mt-6">
                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" data-testid="save-payment-btn">
                  {formData.payment_type === 'advance' ? 'Save Advance' : 'Save Payment'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditingPaymentId(null); setFormData(blankForm()); }} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-2 mb-3" data-testid="payment-filter-chips">
        {[
          { key: 'all', label: 'All' },
          { key: 'invoice_payment', label: 'Invoice Payments' },
          { key: 'advance', label: 'Advances' },
        ].map((c) => (
          <button
            key={c.key}
            onClick={() => setTypeFilter(c.key)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${typeFilter === c.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            data-testid={`filter-chip-${c.key}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="🔍 Search payments..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full border rounded px-3 py-2 mb-2"
      />

      {/* Advanced filters */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4" data-testid="payments-advanced-filters">
        <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="payments-filter-mode">
          <option value="all">All payment modes</option>
          <option value="Cash">Cash</option>
          <option value="Cheque">Cheque</option>
          <option value="Bank Transfer">Bank Transfer</option>
        </select>
        <select value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="payments-filter-customer">
          <option value="all">All customers</option>
          {customerNameOptions.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <DateInputDMY value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="From date" data-testid="payments-filter-from-date" />
        <DateInputDMY value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="To date" data-testid="payments-filter-to-date" />
        {(modeFilter !== 'all' || customerFilter !== 'all' || fromDate || toDate || searchQuery || typeFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { setModeFilter('all'); setCustomerFilter('all'); setFromDate(''); setToDate(''); setSearchQuery(''); setTypeFilter('all'); }}
            className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            data-testid="payments-clear-filters-btn"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Filtered totals — split by invoice payments vs advances */}
      {filteredPayments.length > 0 && (() => {
        // For accurate "Invoice Balance" totals, only count each invoice
        // once — using the LATEST payment row (max received_date / created_at)
        // for that invoice in the filtered set. Otherwise the same balance
        // would be added once per payment row visible.
        const latestRowPerInv = new Map(); // invoice_number → payment with latest date
        for (const p of filteredPayments) {
          if ((p.payment_type || 'invoice_payment') === 'advance') continue;
          const key = p.invoice_number;
          const prev = latestRowPerInv.get(key);
          const pd = p.received_date || '';
          if (!prev || pd > (prev.received_date || '')) {
            latestRowPerInv.set(key, p);
          }
        }
        const t = filteredPayments.reduce((acc, p) => {
          const isAdv = (p.payment_type || 'invoice_payment') === 'advance';
          acc.received += (p.received_amount || 0);
          if (isAdv) {
            acc.advanceCount += 1;
            acc.advanceAmount += (p.received_amount || 0);
          } else {
            acc.invCount += 1;
            acc.invReceived += (p.received_amount || 0);
          }
          return acc;
        }, { received: 0, advanceCount: 0, advanceAmount: 0, invCount: 0, invReceived: 0 });
        t.invBalance = Array.from(latestRowPerInv.values()).reduce((sum, p) => {
          const run = runningByPaymentId[p.id];
          return sum + (run ? run.displayBalance : (p.balance_amount || 0));
        }, 0);
        return (
          <div className="flex flex-wrap items-center gap-3 mb-4 px-3 py-2 rounded-md bg-blue-50 border border-blue-200" data-testid="payments-filtered-totals">
            <span className="text-xs text-gray-700">
              Filtered: <b>{filteredPayments.length}</b> · Invoices: <b>{t.invCount}</b> · Advances: <b>{t.advanceCount}</b>
            </span>
            <span className="text-xs px-2 py-1 rounded bg-white border border-green-200 text-green-800" data-testid="payments-filtered-received">
              Total Received: <b>{formatAmount(t.received)}</b>
            </span>
            <span className="text-xs px-2 py-1 rounded bg-white border border-amber-200 text-amber-800" data-testid="payments-filtered-advances">
              Advances: <b>{formatAmount(t.advanceAmount)}</b>
            </span>
            <span className="text-xs px-2 py-1 rounded bg-white border border-red-200 text-red-800" data-testid="payments-filtered-balance">
              Invoice Balance: <b>{formatAmount(t.invBalance)}</b>
            </span>
          </div>
        );
      })()}

      {/* Payments List */}
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th onClick={() => requestPaySort('invoice_number')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Invoice / PO <span className="text-[10px] text-gray-400">{payInd('invoice_number')}</span></th>
              <th onClick={() => requestPaySort('customer_name')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Customer <span className="text-[10px] text-gray-400">{payInd('customer_name')}</span></th>
              <th onClick={() => requestPaySort('invoice_amount')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Invoice Amt <span className="text-[10px] text-gray-400">{payInd('invoice_amount')}</span></th>
              <th onClick={() => requestPaySort('received_amount')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Received <span className="text-[10px] text-gray-400">{payInd('received_amount')}</span></th>
              <th onClick={() => requestPaySort('balance_amount')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Balance <span className="text-[10px] text-gray-400">{payInd('balance_amount')}</span></th>
              <th onClick={() => requestPaySort('received_date')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Date <span className="text-[10px] text-gray-400">{payInd('received_date')}</span></th>
              <th onClick={() => requestPaySort('payment_mode')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Mode <span className="text-[10px] text-gray-400">{payInd('payment_mode')}</span></th>
              <th onClick={() => requestPaySort('payment_status')} className="px-4 py-2 text-left text-sm font-medium cursor-pointer select-none hover:bg-gray-100">Status <span className="text-[10px] text-gray-400">{payInd('payment_status')}</span></th>
              {(hasPermission(user, 'payments', 'edit') || hasPermission(user, 'payments', 'delete')) && (
                <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedPayments.map(payment => {
              const isAdv = (payment.payment_type || 'invoice_payment') === 'advance';
              // For invoice payments, display the running balance computed
              // across ALL payments on the parent invoice (chronological),
              // not the stored `balance_amount` (which is per-row vs gross).
              const running = !isAdv ? runningByPaymentId[payment.id] : null;
              const displayBalance = running ? running.displayBalance : (payment.balance_amount || 0);
              const displayStatus = running ? running.displayStatus : payment.payment_status;
              return (
              <tr key={payment.id} className={`${payment.cancellation_status === 'pending' ? 'bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-200' : running?.isPendingCheque ? 'bg-orange-50 hover:bg-orange-100' : isAdv ? 'bg-amber-50/30 hover:bg-gray-50' : 'hover:bg-gray-50'}`} data-testid={`payment-row-${payment.id}`}>
                <td className="px-4 py-2 font-medium">
                  {isAdv ? (
                    <span className="text-amber-700" title={payment.applied_invoice_number ? `Applied to ${payment.applied_invoice_number}` : 'Advance against PO'}>
                      💰 PO {payment.po_number || '—'}
                    </span>
                  ) : (
                    <span className="text-blue-600">#{payment.invoice_number}</span>
                  )}
                </td>
                <td className="px-4 py-2">{payment.customer_name}</td>
                <td className="px-4 py-2">{isAdv ? '—' : formatAmount(payment.invoice_amount)}</td>
                <td className="px-4 py-2 font-semibold text-green-600">{formatAmount(payment.received_amount)}</td>
                <td className="px-4 py-2 font-semibold text-red-600">{isAdv ? '—' : formatAmount(displayBalance)}</td>
                <td className="px-4 py-2">{formatDateDMY(payment.received_date)}</td>
                <td className="px-4 py-2">{payment.payment_mode}{payment.payment_mode === 'Bank Transfer' && payment.bank_name ? ` — ${payment.bank_name}` : ''}</td>
                <td className="px-4 py-2">
                  {isAdv ? (
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        payment.advance_status === 'applied' ? 'bg-green-100 text-green-800'
                        : payment.advance_status === 'refunded' ? 'bg-gray-200 text-gray-700'
                        : 'bg-amber-100 text-amber-800'
                      }`}
                      title={payment.applied_invoice_number ? `Applied to ${payment.applied_invoice_number}` : ''}
                      data-testid={`advance-status-${payment.id}`}
                    >
                      {payment.advance_status === 'applied' ? `Applied → ${payment.applied_invoice_number}`
                        : payment.advance_status === 'refunded' ? 'Refunded'
                        : 'Unapplied'}
                    </span>
                  ) : (
                    <span className={`px-2 py-1 rounded text-xs ${displayStatus === 'Full' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {displayStatus}
                    </span>
                  )}
                  {!isAdv && running?.isPendingCheque && (
                    <span
                      className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-orange-100 text-orange-800 font-semibold"
                      title={`Cheque ${payment.cheque_no || ''} dated ${payment.cheque_date || '—'} — NOT YET DEPOSITED. Customer ledger and invoice balance will not update until the deposit date is entered.`}
                      data-testid={`cheque-pending-deposit-${payment.id}`}
                    >
                      ⏰ Cheque pending deposit
                    </span>
                  )}
                  {payment.cancellation_status === 'pending' && (
                    <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-semibold" title={`Cancellation pending — ${payment.cancellation_reason || ''}`}>
                      🟠 Cancel pending
                    </span>
                  )}
                  {payment.cancellation_status === 'cancelled' && (
                    <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-slate-200 text-slate-700 font-semibold" title={payment.cancellation_reason || ''}>
                      🚫 Cancelled
                    </span>
                  )}
                </td>
                {(hasPermission(user, 'payments', 'view') || hasPermission(user, 'payments', 'edit') || hasPermission(user, 'payments', 'delete')) && (
                  <td className="px-4 py-2 whitespace-nowrap">
                    <button
                      onClick={() => setViewingPayment(payment)}
                      className="text-gray-700 hover:text-gray-900 text-sm font-medium mr-3"
                      title="View payment details"
                      data-testid={`view-payment-btn-${payment.id}`}
                    >
                      👁 View
                    </button>
                    <button
                      onClick={() => setPrintingPayment(payment)}
                      className="text-purple-600 hover:text-purple-800 text-sm font-medium mr-3"
                      title="Print payment receipt"
                      data-testid={`print-payment-btn-${payment.id}`}
                    >
                      🖨 Print
                    </button>
                    {isAdv && payment.advance_status === 'unapplied' && hasPermission(user, 'payments', 'edit') && (
                      <button
                        onClick={() => handleRefundAdvance(payment)}
                        className="text-amber-700 hover:text-amber-900 text-sm font-medium mr-3"
                        title="Mark this advance as refunded"
                        data-testid={`refund-advance-btn-${payment.id}`}
                      >
                        ↩️ Refund
                      </button>
                    )}
                    {hasPermission(user, 'payments', 'edit') && (
                      <button
                        onClick={() => handleEditPayment(payment)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                        title="Edit this payment"
                        data-testid={`edit-payment-btn-${payment.id}`}
                      >
                        ✏️ Edit
                      </button>
                    )}
                    {hasPermission(user, 'payments', 'delete') && (
                      <button
                        onClick={() => handleDeletePayment(payment)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium mr-2"
                        title="Delete this payment"
                        data-testid={`delete-payment-btn-${payment.id}`}
                      >
                        🗑 Delete
                      </button>
                    )}
                    <RequestCancellationButton docKind="payment" docId={payment.id} doc={payment} onDone={fetchPayments} />
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Payment Details — read-only viewer */}
      {viewingPayment && (() => {
        const p = viewingPayment;
        const isAdv = (p.payment_type || 'invoice_payment') === 'advance';
        const inv = !isAdv ? invoices.find((i) => i.invoice_number === p.invoice_number) : null;
        // Use the running balance computed across all payments for the
        // parent invoice (chronological) — same rule as the list row.
        const running = !isAdv ? runningByPaymentId[p.id] : null;
        const viewBalance = running ? running.displayBalance : (p.balance_amount || 0);
        const viewStatus = running ? running.displayStatus : p.payment_status;
        const Row = ({ label, value, testid }) => (
          <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-b-0">
            <span className="text-slate-500 text-sm">{label}</span>
            <span className="text-slate-900 text-sm font-medium text-right break-words" data-testid={testid}>{value || '—'}</span>
          </div>
        );
        return (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setViewingPayment(null)} data-testid="payment-view-modal">
            <div className="bg-white rounded-xl w-full max-w-2xl my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
                <h2 className="font-semibold text-lg">
                  {isAdv ? '💰 Advance Receipt' : '🧾 Payment Receipt'}
                  <span className="ml-2 text-slate-500 text-sm font-normal">#{p.id?.slice(0, 8)}</span>
                </h2>
                <button onClick={() => setViewingPayment(null)} className="text-slate-500 hover:text-slate-700 text-2xl leading-none">✕</button>
              </div>
              <div className="p-5 space-y-4">
                {p.cancellation_status === 'pending' && (
                  <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
                    <div className="font-semibold text-amber-900">🟠 Cancellation pending approval</div>
                    <div className="text-amber-800 mt-1"><span className="font-medium">Reason:</span> {p.cancellation_reason || '—'}</div>
                    {p.cancellation_note && <div className="text-amber-800"><span className="font-medium">Note:</span> {p.cancellation_note}</div>}
                  </div>
                )}
                {p.cancellation_status === 'cancelled' && (
                  <div className="rounded border border-slate-300 bg-slate-100 px-3 py-2 text-sm">
                    <div className="font-semibold text-slate-800">🚫 Payment cancelled</div>
                    {p.cancellation_reason && <div className="text-slate-700 mt-1"><span className="font-medium">Reason:</span> {p.cancellation_reason}</div>}
                  </div>
                )}
                {running?.isPendingCheque && (
                  <div className="rounded border border-orange-300 bg-orange-50 px-3 py-2 text-sm" data-testid="view-cheque-pending-banner">
                    <div className="font-semibold text-orange-900">⏰ Cheque pending deposit</div>
                    <div className="text-orange-800 mt-1">
                      This receipt is NOT yet reflected in the customer ledger or the invoice balance. Once the cheque clears the bank, edit this payment and fill in the <b>Deposit Date</b> — the ledger will then auto-update.
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Parties</h3>
                  <Row label="Customer" value={p.customer_name} testid="view-customer" />
                  {isAdv ? (
                    <Row label="Customer's PO Number" value={p.po_number} testid="view-po-number" />
                  ) : (
                    <Row label="Invoice Number" value={p.invoice_number} testid="view-invoice-number" />
                  )}
                  {!isAdv && inv && (
                    <Row label="Invoice Date" value={inv.invoice_date ? formatDateDMY(inv.invoice_date) : '—'} />
                  )}
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Amounts</h3>
                  {!isAdv && <Row label={`Invoice Amount (${currencyCode})`} value={formatAmount(p.invoice_amount)} testid="view-invoice-amount" />}
                  <Row label={`Received (${currencyCode})`} value={<span className="text-emerald-700">{formatAmount(p.received_amount)}</span>} testid="view-received-amount" />
                  {!isAdv && <Row label={`Balance (${currencyCode})`} value={<span className={(viewBalance || 0) > 0.0001 ? 'text-rose-700' : 'text-emerald-700'}>{formatAmount(viewBalance)}</span>} testid="view-balance-amount" />}
                  {isAdv ? (
                    <Row
                      label="Advance Status"
                      value={
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          p.advance_status === 'applied' ? 'bg-green-100 text-green-800'
                          : p.advance_status === 'refunded' ? 'bg-slate-200 text-slate-700'
                          : 'bg-amber-100 text-amber-800'
                        }`}>
                          {p.advance_status === 'applied' ? `Applied → ${p.applied_invoice_number || '?'}`
                            : p.advance_status === 'refunded' ? 'Refunded'
                            : 'Unapplied'}
                        </span>
                      }
                    />
                  ) : (
                    <Row
                      label="Status"
                      value={<span className={`px-2 py-0.5 rounded text-xs ${viewStatus === 'Full' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{viewStatus}</span>}
                    />
                  )}
                  {isAdv && p.applied_on && <Row label="Applied On" value={formatDateDMY(p.applied_on)} />}
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Payment</h3>
                  <Row label="Received Date" value={p.received_date ? formatDateDMY(p.received_date) : '—'} testid="view-received-date" />
                  <Row label="Mode" value={p.payment_mode} testid="view-payment-mode" />
                  {p.payment_mode === 'Cheque' && (
                    <>
                      <Row label="Cheque No" value={p.cheque_no} testid="view-cheque-no" />
                      <Row label="Cheque Date" value={p.cheque_date ? formatDateDMY(p.cheque_date) : '—'} testid="view-cheque-date" />
                      <Row label="Deposit Date" value={p.cheque_deposit_date ? formatDateDMY(p.cheque_deposit_date) : '—'} testid="view-cheque-deposit-date" />
                    </>
                  )}
                  {p.payment_mode === 'Bank Transfer' && (
                    <>
                      <Row label="Transfer Date" value={p.transfer_date ? formatDateDMY(p.transfer_date) : '—'} testid="view-transfer-date" />
                      <Row label="Bank Name" value={p.bank_name} testid="view-bank-name" />
                    </>
                  )}
                  {p.record_no && <Row label="Record No." value={<span className="font-mono">{p.record_no}</span>} testid="view-record-no" />}
                  {p.receipt_no && <Row label="Receipt No" value={p.receipt_no} testid="view-receipt-no" />}
                  {p.notes && <Row label="Notes" value={<span className="whitespace-pre-wrap">{p.notes}</span>} testid="view-notes" />}
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Audit</h3>
                  <Row label="Created At" value={p.created_at ? new Date(p.created_at).toLocaleString() : '—'} />
                  {p.updated_at && <Row label="Last Updated" value={new Date(p.updated_at).toLocaleString()} />}
                  <Row label="Payment ID" value={<span className="font-mono text-xs">{p.id}</span>} />
                </div>
              </div>
              <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2 rounded-b-xl">
                <button
                  onClick={() => setPrintingPayment(p)}
                  className="px-3 py-1.5 text-sm rounded border border-purple-600 text-purple-700 hover:bg-purple-50"
                  data-testid="view-print-receipt"
                >🖨 Print Receipt</button>
                {hasPermission(user, 'payments', 'edit') && (
                  <button
                    onClick={() => { setViewingPayment(null); handleEditPayment(p); }}
                    className="px-3 py-1.5 text-sm rounded border border-blue-600 text-blue-700 hover:bg-blue-50"
                    data-testid="view-edit-shortcut"
                  >✏️ Edit</button>
                )}
                <button
                  onClick={() => setViewingPayment(null)}
                  className="px-3 py-1.5 text-sm rounded bg-slate-800 text-white hover:bg-slate-900"
                  data-testid="view-close-btn"
                >Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment Receipt — print preview iframe modal */}
      {printingPayment && (
        <PaymentReceiptPrint
          payment={printingPayment}
          company={companySettings}
          currencyCode={currencyCode}
          formatAmount={formatAmount}
          onClose={() => setPrintingPayment(null)}
        />
      )}
    </div>
  );
}
